import { useEffect, useMemo, useRef, useState } from 'react';
import { Check, ChevronsUpDown, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { useDebounce } from '@/hooks/useDebounce';
import { useQuoteAccount, useQuoteAccountsInfinite } from '../hooks';

interface AccountFilterComboboxProps {
  value?: string;
  onChange: (accountId: string) => void;
  disabled?: boolean;
  className?: string;
  placeholder?: string;
}

const ALL_VALUE = 'all';

export function AccountFilterCombobox({
  value,
  onChange,
  disabled = false,
  className,
  placeholder,
}: AccountFilterComboboxProps) {
  const { t } = useTranslation('quotes');
  const resetLabel = placeholder ?? t('filters.allCustomers', 'All Accounts');
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);

  const infiniteQuery = useQuoteAccountsInfinite(debouncedSearch);
  const loadedAccounts = useMemo(
    () => infiniteQuery.data?.pages.flatMap((page) => page.accounts) ?? [],
    [infiniteQuery.data],
  );
  const total = infiniteQuery.data?.pages[0]?.total ?? 0;

  const isExplicitValue = !!value && value !== ALL_VALUE;
  const selectedFromLoaded = isExplicitValue
    ? loadedAccounts.find((account) => account._id === value)
    : undefined;
  const fallbackLookupId = isExplicitValue && !selectedFromLoaded ? value : undefined;
  const fallbackQuery = useQuoteAccount(fallbackLookupId);
  const selected = selectedFromLoaded ?? fallbackQuery.data ?? null;

  useEffect(() => {
    if (!open) setSearch('');
  }, [open]);

  const sentinelRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!open) return;
    const node = sentinelRef.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (
          entries[0]?.isIntersecting &&
          infiniteQuery.hasNextPage &&
          !infiniteQuery.isFetchingNextPage
        ) {
          void infiniteQuery.fetchNextPage();
        }
      },
      { threshold: 0.5 },
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [
    open,
    infiniteQuery.hasNextPage,
    infiniteQuery.isFetchingNextPage,
    infiniteQuery.fetchNextPage,
  ]);

  const triggerLabel = selected?.name ?? resetLabel;
  const showingLabel = total > 0
    ? t('sku.showingCount', {
      loaded: loadedAccounts.length,
      total,
      defaultValue: 'Showing {{loaded}} of {{total}}',
    })
    : null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            'h-7 w-full justify-between text-xs font-normal',
            className,
          )}
        >
          <span className="truncate">{triggerLabel}</span>
          <ChevronsUpDown className="ml-1 h-3 w-3 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[260px] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder={t('fields.searchCustomer', 'Search accounts...')}
            value={search}
            onValueChange={setSearch}
          />
          <CommandList>
            <CommandEmpty>
              {infiniteQuery.isError ? (
                <button
                  type="button"
                  className="w-full px-2 py-2 text-left text-xs"
                  onClick={() => {
                    void infiniteQuery.refetch();
                  }}
                >
                  {t('filters.loadFailed', 'Failed to load. Click to retry.')}
                </button>
              ) : infiniteQuery.isLoading ? (
                t('filters.loading', 'Loading...')
              ) : (
                t('fields.noCustomerFound', 'No accounts found.')
              )}
            </CommandEmpty>
            <CommandGroup>
              <CommandItem
                value="__all__"
                onSelect={() => {
                  onChange(ALL_VALUE);
                  setOpen(false);
                }}
              >
                <Check
                  className={cn(
                    'mr-2 h-4 w-4',
                    !value || value === ALL_VALUE ? 'opacity-100' : 'opacity-0',
                  )}
                />
                {resetLabel}
              </CommandItem>
              {loadedAccounts.map((account) => (
                <CommandItem
                  key={account._id}
                  value={account._id}
                  onSelect={() => {
                    onChange(account._id);
                    setOpen(false);
                  }}
                >
                  <Check
                    className={cn(
                      'mr-2 h-4 w-4',
                      value === account._id ? 'opacity-100' : 'opacity-0',
                    )}
                  />
                  <span className="truncate">{account.name}</span>
                </CommandItem>
              ))}
              {infiniteQuery.hasNextPage && (
                <div
                  ref={sentinelRef}
                  className="flex items-center justify-center py-2 text-xs text-muted-foreground"
                >
                  {infiniteQuery.isFetchingNextPage ? (
                    <>
                      <Loader2 className="mr-2 h-3 w-3 animate-spin" />
                      {t('sku.loadingMore', 'Loading more...')}
                    </>
                  ) : (
                    <span>&nbsp;</span>
                  )}
                </div>
              )}
            </CommandGroup>
          </CommandList>
          {showingLabel && (
            <div className="border-t px-2 py-1 text-xs text-muted-foreground">
              {showingLabel}
            </div>
          )}
        </Command>
      </PopoverContent>
    </Popover>
  );
}
